import { useState, useMemo, useCallback, useEffect } from 'react'
import { CaretRightIcon, CaretDownIcon, DotsThreeIcon } from '@phosphor-icons/react'
import {
    ScoopSpinner,
    ScoopSearchBar,
    ScoopMenu,
    ScoopMenuItem,
    ScoopInfoTooltip,
    ScoopText,
    ScoopButton,
    ScoopCheckbox,
} from '..'
import styles from './ScoopTree.module.scss'

/**
 * @typedef {Object} TreeNode
 * @property {string|number} id - Unique identifier for the node
 * @property {string} label - Display text for the node
 * @property {TreeNode[]} [children] - Child nodes
 * @property {string} [tooltip] - Optional tooltip text
 * @property {boolean} [hasChildren] - For lazy loading, indicates if node has children to load
 * @property {*} [key: string] - Any additional custom properties
 */

/**
 * @typedef {Object} MenuItem
 * @property {string} label - Display text for the menu item
 * @property {function(TreeNode): void} onClick - Handler called when menu item is clicked
 * @property {boolean} [disabled] - Whether the menu item is disabled
 */

/**
 * ScoopTree - A flexible tree component with search, selection, and context menus
 *
 * @component
 *
 * @param {Object} props
 * @param {TreeNode[]} [props.data=[]] - Array of tree nodes to display
 *
 * @param {boolean} [props.searchable=false] - Enable search functionality
 * @param {string} [props.searchPlaceholder='Search...'] - Placeholder text for search input
 * @param {string} [props.searchSize='md'] - Size of search input ('xs' | 'sm' | 'md' | 'lg')
 * @param {string} [props.searchWidth] - Custom width for search input
 * @param {string} [props.searchHeight] - Custom height for search input
 * @param {string} [props.searchPadding] - Custom padding around search input
 * @param {function(string): void} [props.onSearch] - Callback when search query changes (debounced 500ms)
 *
 * @param {boolean} [props.selectable=false] - Enable built-in selection with checkboxes
 * @param {boolean} [props.multiSelect=false] - Allow multiple selection (requires selectable=true)
 * @param {TreeNode[]} [props.selectedNodes=[]] - Currently selected nodes (controlled)
 * @param {function(TreeNode[]): void} [props.onSelectionChange] - Callback when selection changes
 *
 * @param {boolean} [props.lazyLoad=false] - Enable lazy loading of children
 * @param {function(string|number): Promise<void>} [props.onLoadChildren] - Async function to load children for a node
 *
 * @param {boolean} [props.contextMenu=false] - Enable context menu on nodes
 * @param {MenuItem[]|function(TreeNode): MenuItem[]} [props.menuItems] - Menu items or function returning items
 *
 * @param {function(TreeNode): React.ReactNode} [props.renderNode] - Custom node content renderer.
 *   When provided, replaces default checkbox + label. Use for custom selection logic.
 *   IMPORTANT: If you need row clicks to trigger selection, either:
 *   1. Use onNodeClick prop to handle clicks, OR
 *   2. Add onClick handlers to your custom content
 *
 * @param {function(TreeNode, number, boolean): React.ReactNode} [props.renderIcon] - Custom icon renderer
 *   Receives (node, level, isExpanded). Replaces default caret icons.
 *
 * @param {function(TreeNode): void} [props.onNodeClick] - Called when ANY node row is clicked.
 *   Always fires regardless of selectable prop. Use this for custom click handling
 *   when using renderNode with your own selection logic.
 *
 * @param {string} [props.nodeIdKey='id'] - Property name for node ID
 * @param {string} [props.nodeLabelKey='label'] - Property name for node label
 * @param {string} [props.nodeChildrenKey='children'] - Property name for node children
 * @param {boolean} [props.expandedByDefault=false] - Expand all nodes on mount
 * @param {string} [props.className=''] - Additional CSS class
 *
 * @example
 * // Basic usage with built-in selection
 * <ScoopTree
 *   data={treeData}
 *   selectable={true}
 *   multiSelect={true}
 *   selectedNodes={selected}
 *   onSelectionChange={setSelected}
 * />
 *
 * @example
 * // Custom renderNode with onNodeClick for selection
 * <ScoopTree
 *   data={treeData}
 *   selectable={false}
 *   onNodeClick={(node) => handleCustomSelection(node)}
 *   renderNode={(node) => (
 *     <ScoopRow>
 *       <ScoopCheckbox checked={isSelected(node)} onChange={() => {}} />
 *       <span>{node.label}</span>
 *     </ScoopRow>
 *   )}
 * />
 *
 * @example
 * // With search and lazy loading
 * <ScoopTree
 *   data={treeData}
 *   searchable={true}
 *   searchPlaceholder="Search items..."
 *   lazyLoad={true}
 *   onLoadChildren={async (nodeId) => await fetchChildren(nodeId)}
 * />
 */
export const ScoopTree = ({
    data = [],
    searchable = false,
    searchPlaceholder = 'Search...',
    searchSize = 'md',
    searchWidth,
    searchHeight,
    searchPadding,
    onSearch,
    selectable = false,
    multiSelect = false,
    selectedNodes = [],
    onSelectionChange,
    lazyLoad = false,
    onLoadChildren,
    contextMenu = false,
    menuItems,
    renderNode,
    renderIcon,
    onNodeClick,
    nodeIdKey = 'id',
    nodeLabelKey = 'label',
    nodeChildrenKey = 'children',
    expandedByDefault = false,
    className = '',
    ...props
}) => {
    const [searchQuery, setSearchQuery] = useState('')
    const [expandedNodes, setExpandedNodes] = useState(new Set())
    const [loadingNodes, setLoadingNodes] = useState(new Set())
    const [contextMenuAnchor, setContextMenuAnchor] = useState(null)
    const [contextMenuNode, setContextMenuNode] = useState(null)

    // Initialize expanded state if expandedByDefault
    useEffect(() => {
        if (expandedByDefault && data.length > 0) {
            const allNodeIds = new Set()
            const collectIds = (nodes) => {
                nodes.forEach((node) => {
                    if (node[nodeChildrenKey]?.length > 0) {
                        allNodeIds.add(node[nodeIdKey])
                        collectIds(node[nodeChildrenKey])
                    }
                })
            }
            collectIds(data)
            setExpandedNodes(allNodeIds)
        }
    }, [data, expandedByDefault, nodeIdKey, nodeChildrenKey])

    // Debounced search handler
    useEffect(() => {
        if (!searchable || !onSearch) return

        const timer = setTimeout(() => {
            onSearch(searchQuery)
        }, 500)

        return () => clearTimeout(timer)
    }, [searchQuery, onSearch, searchable])

    // Filter tree based on search query
    const filteredData = useMemo(() => {
        if (!searchQuery.trim()) return data

        const filterNodes = (nodes) => {
            return nodes.reduce((acc, node) => {
                const label = node[nodeLabelKey] || ''
                const matchesSearch = label.toLowerCase().includes(searchQuery.toLowerCase().trim())
                const children = node[nodeChildrenKey] ? filterNodes(node[nodeChildrenKey]) : []

                if (matchesSearch || children.length > 0) {
                    acc.push({
                        ...node,
                        [nodeChildrenKey]: children,
                    })
                }

                return acc
            }, [])
        }

        return filterNodes(data)
    }, [data, searchQuery, nodeLabelKey, nodeChildrenKey])

    // Auto-expand nodes when searching
    useEffect(() => {
        if (!searchQuery.trim()) {
            if (expandedByDefault && data.length > 0) {
                const allNodeIds = new Set()
                const collectIds = (nodes) => {
                    nodes.forEach((node) => {
                        if (node[nodeChildrenKey]?.length > 0) {
                            allNodeIds.add(node[nodeIdKey])
                            collectIds(node[nodeChildrenKey])
                        }
                    })
                }
                collectIds(data)
                setExpandedNodes(allNodeIds)
            }
            return
        }

        const nodesToExpand = new Set()
        const collectExpandableNodes = (nodes) => {
            nodes.forEach((node) => {
                const children = node[nodeChildrenKey]
                if (children && children.length > 0) {
                    nodesToExpand.add(node[nodeIdKey])
                    collectExpandableNodes(children)
                }
            })
        }
        collectExpandableNodes(filteredData)
        setExpandedNodes(nodesToExpand)
    }, [searchQuery, filteredData, nodeIdKey, nodeChildrenKey, expandedByDefault, data])

    /**
     * Toggle node expansion state
     * @param {TreeNode} node - The node to expand/collapse
     */
    const handleToggleExpand = useCallback(
        async (node) => {
            const nodeId = node[nodeIdKey]
            const isExpanded = expandedNodes.has(nodeId)

            if (isExpanded) {
                setExpandedNodes((prev) => {
                    const next = new Set(prev)
                    next.delete(nodeId)
                    return next
                })
            } else {
                setExpandedNodes((prev) => new Set(prev).add(nodeId))

                if (lazyLoad && onLoadChildren && !node[nodeChildrenKey]) {
                    setLoadingNodes((prev) => new Set(prev).add(nodeId))

                    try {
                        await onLoadChildren(nodeId)
                    } catch (error) {
                        console.error('Failed to load children:', error)
                    } finally {
                        setLoadingNodes((prev) => {
                            const next = new Set(prev)
                            next.delete(nodeId)
                            return next
                        })
                    }
                }
            }
        },
        [expandedNodes, lazyLoad, onLoadChildren, nodeIdKey, nodeChildrenKey]
    )

    /**
     * Handle built-in node selection (when selectable=true)
     * @param {TreeNode} node - The node to select/deselect
     */
    const handleNodeSelect = useCallback(
        (node) => {
            if (!selectable || !onSelectionChange) return

            const nodeId = node[nodeIdKey]
            const isSelected = selectedNodes.some((n) => n[nodeIdKey] === nodeId)

            if (multiSelect) {
                const newSelection = isSelected
                    ? selectedNodes.filter((n) => n[nodeIdKey] !== nodeId)
                    : [...selectedNodes, node]
                onSelectionChange(newSelection)
            } else {
                const newSelection = isSelected ? [] : [node]
                onSelectionChange(newSelection)
            }
        },
        [selectable, multiSelect, selectedNodes, onSelectionChange, nodeIdKey]
    )

    /**
     * Handle row click - expands/collapses, selects (if enabled), and calls onNodeClick
     * @param {TreeNode} node - The clicked node
     * @param {boolean} hasChildren - Whether the node has children
     */
    const handleRowClick = useCallback(
        (node, hasChildren) => {
            // 1. Expand/collapse if has children
            if (hasChildren) {
                handleToggleExpand(node)
            }
            // 2. Toggle selection if selectable (built-in selection)
            if (selectable) {
                handleNodeSelect(node)
            }
            // 3. Always call onNodeClick if provided (for custom handling)
            onNodeClick?.(node)
        },
        [handleToggleExpand, handleNodeSelect, selectable, onNodeClick]
    )

    /**
     * Open context menu for a node
     */
    const handleContextMenu = useCallback(
        (node, event) => {
            if (!contextMenu || !menuItems) return

            event.preventDefault()
            event.stopPropagation()

            setContextMenuNode(node)
            setContextMenuAnchor(event.currentTarget)
        },
        [contextMenu, menuItems]
    )

    /**
     * Close context menu
     */
    const handleCloseContextMenu = useCallback(() => {
        setContextMenuAnchor(null)
        setContextMenuNode(null)
    }, [])

    /**
     * Render a single tree node
     */
    const renderTreeNode = useCallback(
        (node, level = 0) => {
            const nodeId = node[nodeIdKey]
            const nodeLabel = node[nodeLabelKey] || ''
            const nodeChildren = node[nodeChildrenKey] || []
            const hasChildren = nodeChildren.length > 0 || (lazyLoad && node.hasChildren)
            const isExpanded = expandedNodes.has(nodeId)
            const isLoading = loadingNodes.has(nodeId)
            const isSelected = selectable && selectedNodes.some((n) => n[nodeIdKey] === nodeId)

            const paddingLeft = level * 20 + 8

            return (
                <div key={nodeId} className={styles.treeNode}>
                    <div
                        className={`${styles.nodeContent} ${isSelected ? styles.selected : ''}`}
                        style={{ paddingLeft: `${paddingLeft}px` }}
                        onClick={() => handleRowClick(node, hasChildren)}
                    >
                        {/* Expand/Collapse Button or Custom Icon */}
                        {hasChildren ? (
                            <ScoopButton
                                variant="ghost"
                                size="xs"
                                className={styles.expandButton}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    handleToggleExpand(node)
                                }}
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <ScoopSpinner size={16} />
                                ) : renderIcon ? (
                                    renderIcon(node, level, isExpanded)
                                ) : isExpanded ? (
                                    <CaretDownIcon size={16} weight="bold" />
                                ) : (
                                    <CaretRightIcon size={16} weight="bold" />
                                )}
                            </ScoopButton>
                        ) : renderIcon ? (
                            <div className={styles.iconWrapper}>
                                {renderIcon(node, level, false)}
                            </div>
                        ) : (
                            <div className={styles.spacer} />
                        )}

                        {/* Node Label with optional checkbox */}
                        <div className={styles.nodeLabel}>
                            {renderNode ? (
                                // Custom render - parent handles selection via onNodeClick
                                renderNode(node)
                            ) : (
                                // Default render - built-in checkbox + label
                                <>
                                    {selectable && (
                                        <div onClick={(e) => e.stopPropagation()}>
                                            <ScoopCheckbox
                                                checked={isSelected}
                                                onChange={() => handleNodeSelect(node)}
                                                size="sm"
                                            />
                                        </div>
                                    )}
                                    <ScoopText as="span" className={styles.labelText}>
                                        {nodeLabel}
                                    </ScoopText>
                                </>
                            )}
                        </div>

                        {/* Node Tooltip */}
                        {node.tooltip && (
                            <ScoopInfoTooltip title={node.tooltip} placement="right" />
                        )}

                        {/* Context Menu Button */}
                        {contextMenu && menuItems && (
                            <ScoopButton
                                variant="ghost"
                                size="xs"
                                className={styles.contextMenuButton}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    handleContextMenu(node, e)
                                }}
                            >
                                <DotsThreeIcon size={16} weight="bold" />
                            </ScoopButton>
                        )}
                    </div>

                    {/* Render children if expanded */}
                    {hasChildren && isExpanded && (
                        <div className={styles.nodeChildren}>
                            {nodeChildren.map((child) => renderTreeNode(child, level + 1))}
                        </div>
                    )}
                </div>
            )
        },
        [
            nodeIdKey,
            nodeLabelKey,
            nodeChildrenKey,
            expandedNodes,
            loadingNodes,
            selectedNodes,
            selectable,
            contextMenu,
            menuItems,
            lazyLoad,
            renderNode,
            renderIcon,
            handleRowClick,
            handleToggleExpand,
            handleNodeSelect,
            handleContextMenu,
        ]
    )

    // Get menu items for context menu
    const contextMenuItemsList = useMemo(() => {
        if (!contextMenuNode || !menuItems) return []
        return typeof menuItems === 'function' ? menuItems(contextMenuNode) : menuItems
    }, [contextMenuNode, menuItems])

    return (
        <div className={`${styles.treeContainer} ${className}`} {...props}>
            {/* Search Bar */}
            {searchable && (
                <div
                    className={styles.searchWrapper}
                    style={searchPadding ? { padding: searchPadding } : undefined}
                >
                    <ScoopSearchBar
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={searchPlaceholder}
                        size={searchSize}
                        width={searchWidth}
                        height={searchHeight}
                        className={styles.searchBar}
                    />
                </div>
            )}

            {/* Tree Nodes */}
            <div className={styles.treeContent}>
                {filteredData.length > 0 ? (
                    filteredData.map((node) => renderTreeNode(node, 0))
                ) : (
                    <div className={styles.emptyState}>
                        <ScoopText className={styles.emptyText}>
                            {searchQuery ? 'No results found' : 'No data available'}
                        </ScoopText>
                    </div>
                )}
            </div>

            {/* Context Menu */}
            {contextMenu && (
                <ScoopMenu
                    anchorEl={contextMenuAnchor}
                    open={Boolean(contextMenuAnchor)}
                    onClose={handleCloseContextMenu}
                >
                    {contextMenuItemsList.map((item, index) => (
                        <ScoopMenuItem
                            key={index}
                            onClick={() => {
                                item.onClick(contextMenuNode)
                                handleCloseContextMenu()
                            }}
                            disabled={item.disabled}
                        >
                            {item.label}
                        </ScoopMenuItem>
                    ))}
                </ScoopMenu>
            )}
        </div>
    )
}
